import type { SettingsRow } from '@bn-games/ui-shell';
import type { Language, Settings, Variant } from '../settings/types.js';
import { t } from '../i18n/I18n.js';

const LANGUAGES: readonly Language[] = ['en', 'ru'];
const VARIANTS: readonly Variant[] = ['simplified', 'classical'];

/** Minimal contract the row builder needs — `MahjongMenuContext` satisfies it. */
export interface SettingsRowCtx {
  getSettings(): Settings;
  updateSettings(partial: Partial<Settings>): void;
}

/**
 * The coach toggle (T10), the language selector, and the iteration-2 variant
 * selector (simplified vs. classical — T16; the Play menu also exposes this
 * same setting, see `ui/screens/PlayMenu.ts`). Theme is omitted — there is
 * only one theme (green felt), so a one-option selector would be inert; a
 * second theme reintroduces the row.
 */
export function buildSettingsRows(ctx: SettingsRowCtx): readonly SettingsRow[] {
  const s = ctx.getSettings();
  return [
    {
      kind: 'toggle',
      key: 'coachEnabled',
      label: t('settings.coach'),
      value: s.coachEnabled,
      onClick: () => ctx.updateSettings({ coachEnabled: !s.coachEnabled }),
    },
    {
      kind: 'toggle',
      key: 'turnStartSound',
      label: t('settings.turnStartSound'),
      value: s.turnStartSound,
      onClick: () => ctx.updateSettings({ turnStartSound: !s.turnStartSound }),
    },
    {
      kind: 'selector',
      key: 'language',
      label: t('settings.language'),
      value: s.language,
      options: LANGUAGES,
      format: (v) => t(`settings.languages.${v as Language}`),
      onChange: (next) => ctx.updateSettings({ language: next as Language }),
    },
    {
      kind: 'selector',
      key: 'variant',
      label: t('settings.variant'),
      value: s.variant,
      options: VARIANTS,
      format: (v) => t(`settings.variants.${v as Variant}`),
      onChange: (next) => ctx.updateSettings({ variant: next as Variant }),
    },
  ];
}
